// src/lib/constants.ts - Application constants
// Icons only needed for SPORTS constants, other navigation icons moved to navigation.ts
import { CircleDot, Square, RectangleHorizontal, Zap, Circle } from 'lucide-react';

import type { CourtSportType, UserRoleType, UserStatusType, SubscriptionType, SubscriptionStatusType, SpaceTypeType, ActivityAttributeType, MedicalAttributeType, SubscriptionDuration, OrganizationStatusType } from './types';

// NavItem and NAV_ITEMS are now in src/lib/navigation.ts

export type Sport = {
  id: string;
  name: string;
  icon: React.ElementType;
  color?: string;
};

export const SPORTS: Sport[] = [
  { id: 'tennis', name: 'Tennis', icon: CircleDot, color: 'hsl(209, 78%, 65%)' }, // Pastel Blue
  { id: 'padel', name: 'Padel', icon: Square, color: 'hsl(30, 100%, 85%)' },   // Pastel Orange (Peach)
  { id: 'pickleball', name: 'Pickleball', icon: RectangleHorizontal, color: 'hsl(120, 60%, 80%)' }, // Pastel Green
  { id: 'badminton', name: 'Badminton', icon: Zap, color: 'hsl(240, 60%, 90%)' }, // Pastel Purple (Lavender)
  { id: 'foot5', name: 'Foot5', icon: Circle, color: 'hsl(0, 70%, 85%)' }, // Pastel Red
];

// This list is used for court-specific sport type selection
export const COURT_SPORT_TYPES: CourtSportType[] = (() => {
  try {
    const types = SPORTS.map(sport => sport.name as CourtSportType);
    return types;
  } catch (error) {
    console.error("Error generating COURT_SPORT_TYPES from SPORTS:", error);
    // Fallback to hardcoded values if there's an error
    return ['Tennis', 'Padel', 'Pickleball', 'Badminton', 'Foot5'] as CourtSportType[];
  }
})();

export const SPACE_TYPES: SpaceTypeType[] = ['Court', 'Room', 'Activity', 'Medical'];

export const ACTIVITY_ATTRIBUTES: ActivityAttributeType[] = ['Climbing Wall', 'American Pool', 'Fitness', 'Yoga', 'Pilates'];

export const MEDICAL_ATTRIBUTES: MedicalAttributeType[] = ['Massage', 'Physiotherapy', 'Chiropractor'];

export type Language = {
  code: string;
  name: string;
};

export const LANGUAGES: Language[] = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'de-DE', name: 'Deutsch (Deutschland)' },
  { code: 'de-CH', name: 'Deutsch (Schweiz)' },
  { code: 'fr-FR', name: 'Français (France)' },
  { code: 'fr-CH', name: 'Français (Suisse)' },
  { code: 'it-IT', name: 'Italiano (Italia)' },
  { code: 'it-CH', name: 'Italiano (Svizzera)' },
  { code: 'es-ES', name: 'Español (España)' },
  { code: 'pt-PT', name: 'Português (Portugal)' },
  { code: 'nl-NL', name: 'Nederlands (Nederland)' },
  { code: 'sv-SE', name: 'Svenska (Sverige)' },
  { code: 'da-DK', name: 'Dansk (Danmark)' },
  { code: 'no-NO', name: 'Norsk (Norge)' },
  { code: 'fi-FI', name: 'Suomi (Suomi)' },
];

// User roles, excluding Super Admin as it's not assignable via UI from regular admin pages
export const USER_ROLES: UserRoleType[] = ['Super Admin', 'Admin', 'Staff', 'Member', 'Guest', 'Teacher', 'Tennis Club', 'Tennis School'];

// Roles for Users page - only players and guests (no Admin/Staff/Super Admin)
export const USER_PAGE_ROLES: UserRoleType[] = ['Member', 'Guest', 'Teacher', 'Tennis School', 'Tennis Club'];

// Roles for Staff page - includes administrative roles (excluding Super Admin)
export const STAFF_PAGE_ROLES: UserRoleType[] = ['Admin', 'Staff', 'Teacher'];

// Utility functions for role checking
export const isStaffRole = (role: UserRoleType): boolean => STAFF_PAGE_ROLES.includes(role);
export const isUserRole = (role: UserRoleType): boolean => USER_PAGE_ROLES.includes(role);
export const hasStaffRole = (roles: UserRoleType[]): boolean => roles.some(role => isStaffRole(role));
export const hasUserRole = (roles: UserRoleType[]): boolean => roles.some(role => isUserRole(role));

export const USER_STATUSES: UserStatusType[] = ['Active', 'Archived', 'Suspended'];

export const SUBSCRIPTION_TYPES: SubscriptionType[] = ['Club Membership', 'Booking Pass', 'Premium Access'];
export const SUBSCRIPTION_STATUSES: SubscriptionStatusType[] = ['Active', 'Expired', 'Cancelled', 'Pending Payment', 'Inactive'];
export const SUBSCRIPTION_DURATIONS: SubscriptionDuration[] = ['Yearly', '6-Months', '3-Months', 'Monthly'];

export const ORGANIZATION_STATUSES: OrganizationStatusType[] = ['Active', 'Inactive'];

// COUNTRIES array is moved to platformConfigService.ts

export const SPACE_STATUS_OPTIONS = [
  { value: "All", labelKey: "filters.all_statuses" },
  { value: "active", labelKey: "status.active" },
  { value: "inactive", labelKey: "status.inactive" },
  { value: "maintenance", labelKey: "status.maintenance" }
];

export const COURT_TYPE_OPTIONS = [
  { value: "All", labelKey: "filters.all_types" },
  { value: "Tennis", label: "Tennis" },
  { value: "Badminton", label: "Badminton" },
  { value: "Basketball", label: "Basketball" },
  { value: "Squash", label: "Squash" }
];

export const FACILITY_TYPE_OPTIONS = [
  { value: "All", labelKey: "filters.all_facilities" },
  { value: "Indoor", label: "Indoor" },
  { value: "Outdoor", label: "Outdoor" }
];

export const LIGHTING_OPTIONS = [
  { value: "All", labelKey: "filters.all" },
  { value: "true", labelKey: "filters.with_lights" },
  { value: "false", labelKey: "filters.without_lights" }
];
